import { City, Country, State } from 'country-state-city';

export interface LocationOption {
    label: string;
    isoCode: string;
}

// Build once — this is static data.
export const COUNTRY_OPTIONS: LocationOption[] = Country.getAllCountries().map(c => ({ label: c.name, isoCode: c.isoCode }));

const PINNED_COUNTRY_ISO_CODES = ['US'];

export const PINNED_FIRST_COUNTRY_OPTIONS: LocationOption[] = [
    ...COUNTRY_OPTIONS.filter(c => PINNED_COUNTRY_ISO_CODES.includes(c.isoCode)),
    ...COUNTRY_OPTIONS.filter(c => !PINNED_COUNTRY_ISO_CODES.includes(c.isoCode)),
];

export function getStateOptions(countryIsoCode: string): LocationOption[] {
    return State.getStatesOfCountry(countryIsoCode).map(s => ({ label: s.name, isoCode: s.isoCode }));
}

export function getCityOptions(countryIsoCode: string, stateIsoCode: string): string[] {
    return City.getCitiesOfState(countryIsoCode, stateIsoCode).map(c => c.name);
}

// Countries where an address without a postal code will not be delivered. Most of the
// world is fine without one, so this gates a required-field check, not a format check.
// Mirrors POSTAL_CODE_REQUIRED_COUNTRY_CODES in src/faves/user/model.py — keep both in sync.
export const POSTAL_CODE_REQUIRED_COUNTRIES = new Set(['US']);

export function postalCodeRequired(countryIsoCode: string): boolean {
    return POSTAL_CODE_REQUIRED_COUNTRIES.has(countryIsoCode);
}

// The one place the mailing-address rule lives on the client. Mirrors the server in
// portal_user/handler.py _parse_mailing_address. Three surfaces collect an address -
// the Settings book, the seeding card and the onboarding step - and they render with
// different design systems, so they share this rather than a component.
// Returns the message to show, or null when the address is usable.
export function validateMailingAddress(fields: {
    address_line1: string;
    city: string;
    country_code: string;
    postal_code: string;
}): string | null {
    if (!fields.country_code) { return 'Select a country'; }
    if (!fields.address_line1.trim()) { return 'Street address is required'; }
    if (!fields.city.trim()) { return 'City is required'; }
    if (postalCodeRequired(fields.country_code) && !fields.postal_code.trim()) {
        return 'ZIP code is required for US addresses';
    }
    return null;
}

// Resolve isoCode from a stored name (for restoring state on prefill)
export function countryIsoFromName(name: string): string {
    return COUNTRY_OPTIONS.find(c => c.label === name)?.isoCode ?? '';
}

export function stateIsoFromName(countryIso: string, name: string): string {
    return getStateOptions(countryIso).find(s => s.label === name)?.isoCode ?? '';
}

// Inverse of stateIsoFromName. faves.user_mailing_information stores zone_code (the
// ISO 3166-2 code), while the seeding opt-in form stores the subdivision name, so
// anything moving between the two converts here.
//
// Falls back to the input rather than ''. Not every stored zone_code is an ISO code:
// the brand-application form takes State/Province as free text (see APPLY_STEPS in
// brand_partner/form_schema.py), and those rows are what the mailing-address backfill
// picks up. Returning '' there would drop the state from the address entirely - on the
// card, and on the snapshot that becomes a shipping label.
export function stateNameFromIso(countryIso: string, isoCode: string): string {
    return getStateOptions(countryIso).find(s => s.isoCode === isoCode)?.label ?? isoCode;
}

// Inverse of countryIsoFromName. Falls back to the code so an unrecognised one still
// renders something rather than vanishing from a displayed address.
export function countryNameFromIso(isoCode: string): string {
    return COUNTRY_OPTIONS.find(c => c.isoCode === isoCode)?.label ?? isoCode;
}

// A displayable address, already normalized: `region` is the subdivision NAME, so a
// caller holding an ISO 3166-2 code converts with stateNameFromIso first.
export interface AddressParts {
    line1: string | null;
    line2: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    countryIso: string | null;
}

// Street / locality / country - the three lines the Settings card and the seeding card
// both render. Empty lines are dropped rather than left blank.
export function formatAddressLines(parts: AddressParts): string[] {
    const street = [parts.line1, parts.line2].filter(Boolean).join(', ');
    const locality = [
        [parts.city, parts.region].filter(Boolean).join(', '),
        parts.postalCode ?? '',
    ].filter(Boolean).join(' ');
    return [
        street,
        locality,
        parts.countryIso ? countryNameFromIso(parts.countryIso) : '',
    ].filter(Boolean);
}

// Regional indicator symbols: each ASCII letter maps to U+1F1E6 + (letter - 'A'),
// and a browser pairs them into one flag glyph. Callers should go through the
// CountryFlag component rather than this directly - Windows ships no flag glyphs
// and needs the ISO fallback that component handles.
export function flagEmojiFromIso(iso: string): string {
    if (iso.length !== 2) {
        return '';
    }
    return String.fromCodePoint(...[...iso.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}
